/**
 * Gallery `/` — the public landing. Live-subscribes to public creations, folds
 * them with the native-game manifest through the PURE {@link buildGallery}, and
 * renders a settings-curated Featured row plus sort tabs + style chips + creator
 * filter + search over a responsive grid. All ordering/filtering is delegated to
 * the pure model so it is tested independently of React.
 *
 * The grid paints ONCE: both async inputs (the creations subscription's first
 * emission + the settings read) gate a single loading state, because painting
 * the static native manifest first and reshuffling when creations/featured land
 * reads as a flash of the wrong list on every page load.
 *
 * Beside the catalog sits the {@link PresenceRail} — who is around and what
 * they are playing — as a sticky right sidebar. It is source-ordered after the
 * catalog so narrow viewports move it to the bottom instead of crowding the
 * discovery controls. The rail owns its own subscription and is therefore
 * independent of the grid's single loading gate.
 *
 * Above the full "All Games" catalog stack the four discovery shelves
 * ({@link GalleryShelves}). Catalog-only style/sort/creator controls live with
 * All Games and collapse behind one mobile button.
 */
import { useMemo, useState } from 'react';
import type { Creation } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import {
  buildGallery,
  creationToItem,
  featuredItems,
  editorsPickItems,
  nativeToItem,
  creatorHandles,
  STYLES,
  type GalleryFilter,
  type GalleryItem,
  type GallerySort,
} from '../gallery/model.js';
import { NATIVE_GAMES } from '../gallery/nativeGames.js';
import { GameCard } from '../components/GameCard.js';
import { GalleryShelves } from '../components/GalleryShelves.js';
import { Mascot, MascotTile } from '../components/Mascot.js';
import { PresenceRail } from '../components/PresenceRail.js';
import { CREATION_STYLE_LABELS, type CreationStyle } from '@djgg/creations';
import { useSettings } from '../hooks/useSettings.js';

export function Gallery() {
  const services = useServices();
  const { user } = useAuth();
  // `null` = the subscription has not emitted yet (vs `[]` = genuinely empty).
  const creationDocs = useSubscription<Array<Creation & { id: string }> | null>(
    services.subscribe.creations,
    null,
    [],
  );
  const { settings, loaded: settingsLoaded } = useSettings();
  const loading = creationDocs === null || !settingsLoaded;
  const docs = useMemo(() => creationDocs ?? [], [creationDocs]);
  const docsBySlug = useMemo(() => new Map(docs.map((doc) => [doc.slug, doc])), [docs]);
  // This is deliberately member-only (creator or invited editor), matching the
  // Studio's membership query. A signed-in non-member must not get a shortcut
  // into a game they cannot edit.
  const editHrefFor = (item: GalleryItem): string | undefined => {
    const doc = docsBySlug.get(item.slug);
    if (!user || user.isAnonymous || !doc) return undefined;
    return doc.creatorUid === user.uid || doc.memberUids.includes(user.uid)
      ? `/studio/g/${doc.id}`
      : undefined;
  };

  // Default to "Top rated" — the only order that knows whether a game actually
  // WORKS, and the only one that drops the unplayable outright. Every other
  // sort ranks what creators did (published, iterated, updated) or a lifetime
  // total; with plays in the 0-9 range across the whole catalogue those are
  // noise, which is how a game with dead controls came to sit joint-first.
  // ("Latest" — most-recently-updated — is still one tab away.)
  const [sort, setSort] = useState<GallerySort>('rated');
  const [style, setStyle] = useState<CreationStyle | undefined>(undefined);
  const [creator, setCreator] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Native games have REAL creations docs (kind='native', backing the game
  // page's social surface) that this public-creations subscription also
  // matches — their gallery cards come from the NATIVE_GAMES manifest (so the
  // docs never list twice), with the docs' live counters merged in for the
  // card's stats row.
  const creationItems = useMemo(
    () => docs.filter((c) => c.kind !== 'native').map(creationToItem),
    [docs],
  );
  const nativeItems = useMemo(() => {
    const docsBySlug = new Map(
      docs.filter((c) => c.kind === 'native').map((c) => [c.slug, c]),
    );
    return NATIVE_GAMES.map((g) => nativeToItem(g, docsBySlug.get(g.slug)));
  }, [docs]);
  const handles = useMemo(
    () => creatorHandles([...creationItems, ...nativeItems]),
    [creationItems, nativeItems],
  );

  const filter: GalleryFilter = { sort, style, creatorHandle: creator, search };
  const items = useMemo(
    () => buildGallery(creationItems, nativeItems, filter),
    [creationItems, nativeItems, sort, style, creator, search],
  );

  // The pinned Featured row (R3-3): superadmin-curated slugs resolved against
  // the SAME public-creations subscription + live-only predicate as the main
  // grid (no extra query). Featured games also stay in the grid below.
  const featured = useMemo(
    () => featuredItems(creationItems, settings.galleryFeaturedSlugs),
    [creationItems, settings.galleryFeaturedSlugs],
  );

  // The first "Editors' Pick" discovery shelf: the LLM-reviewed weekly list. Derived
  // purely from the SAME subscription (each pick carries its `editorsPick.current`
  // mark), ordered by rank — no extra query. Natives are eligible picks too, so
  // the full pool feeds it. Picks also stay in the grid below — the grid is the
  // complete catalog (sort/search/filters must still find them).
  const editorsPicks = useMemo(
    () => editorsPickItems([...creationItems, ...nativeItems]),
    [creationItems, nativeItems],
  );

  return (
    <div className="container">
      <div className="gallery__hero gallery__hero--landing">
        <h1 className="gallery__title">
          Play something <em>nobody's ever made</em> before.
        </h1>
        <p className="gallery__subtitle">
          Fresh from the oven — every game here is hand-made (by a person and a bird).
        </p>
        <div className="gallery__searchzone">
          <svg
            className="gallery__search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.4-3.4" />
          </svg>
          <input
            className="input search"
            placeholder="Search games…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search games"
          />
        </div>
      </div>

      {loading ? (
        <div className="empty">Loading games…</div>
      ) : (
        <>
          {settings.motd && <div className="motd">{settings.motd}</div>}

          <div className="gallery-page">
            <div className="gallery-page__main">
              <GalleryShelves
                creations={creationItems}
                editorsPicks={editorsPicks}
                editHrefFor={editHrefFor}
              />

              {featured.length > 0 && (
                <>
                  <h2 className="panel__title">Featured</h2>
                  <div className="grid" style={{ paddingBottom: 28 }} data-testid="featured-grid">
                    {featured.map((item) => (
                      <GameCard key={`featured:${item.slug}`} item={item} editHref={editHrefFor(item)} />
                    ))}
                  </div>
                </>
              )}

              <div className="gallery-shelf__head gallery-catalog__head">
                <h2 className="shelftitle shelftitle--paper">🎮 All Games</h2>
                <button
                  type="button"
                  className="btn btn--sm gallery-filters__toggle"
                  aria-expanded={mobileFiltersOpen}
                  aria-controls="gallery-catalog-controls"
                  onClick={() => setMobileFiltersOpen((open) => !open)}
                >
                  {mobileFiltersOpen ? 'Hide filters' : 'Filter & sort'}
                </button>
              </div>

              <div
                id="gallery-catalog-controls"
                className={`gallery-catalog__controls${mobileFiltersOpen ? ' gallery-catalog__controls--open' : ''}`}
              >
                <div
                  className="chips gallery-catalog__styles"
                  role="group"
                  aria-label="Filter by game type"
                >
                  <button
                    className={`chip ${!style ? 'chip--active' : ''}`}
                    onClick={() => setStyle(undefined)}
                  >
                    all
                  </button>
                  {STYLES.map((s) => (
                    <button
                      key={s}
                      className={`chip ${style === s ? 'chip--active' : ''}`}
                      onClick={() => setStyle(style === s ? undefined : s)}
                    >
                      {CREATION_STYLE_LABELS[s]}
                    </button>
                  ))}
                </div>

                <div className="toolbar gallery-catalog__toolbar">
                  <div className="tabs" role="tablist" aria-label="Sort">
                    {([
                      ['rated', 'Top rated'],
                      ['trending', 'Trending'],
                      ['newest', 'Latest'],
                      ['created', 'Last created'],
                      ['liked', 'Most liked'],
                      ['played', 'Most played'],
                      ['lastPlayed', 'Last played'],
                      ['iterated', 'Most iterated'],
                    ] as const).map(([value, label]) => (
                      <button
                        key={value}
                        role="tab"
                        aria-selected={sort === value}
                        className={`tab ${sort === value ? 'tab--active' : ''}`}
                        onClick={() => setSort(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {handles.length > 0 && (
                    <select
                      className="select gallery-catalog__creator"
                      value={creator ?? ''}
                      onChange={(e) => setCreator(e.target.value || undefined)}
                      aria-label="Filter by creator"
                    >
                      <option value="">All creators</option>
                      {handles.map((h) => (
                        <option key={h} value={h}>
                          @{h}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {items.length === 0 ? (
                <div className="empty">
                  <Mascot context="gallery-empty" />
                  No games match your filters.
                </div>
              ) : (
                <div className="grid" data-testid="gallery-grid">
                  {items.map((item) => (
                    <GameCard key={`${item.kind}:${item.slug}`} item={item} editHref={editHrefFor(item)} />
                  ))}
                  <MascotTile />
                </div>
              )}
            </div>

            <aside className="gallery-page__side">
              <PresenceRail />
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
